import { useMemo, useRef } from 'react';

import { InternalAmenityGroup } from '@nocowanie/noc-api';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile';
import { ACCOMMODATION_TILE } from '@app/consts';
import {
    AmenityTranslations,
    getOverflowWord,
} from '@app/helpers/accommodation-tile-amenities.helpers';
import { getAmenityIcon } from '@app/helpers/get-amenity-icon';
import { filterAmenities, sortAmenities } from '@app/helpers/sort-filter-amenities';
import { useAmenitiesVisibility } from '@app/hooks/useAmenitiesVisibility';
import { InternalAmenityGroupEnum } from '@app/models/api/api-types';
import { AmenityGroupModel } from '@app/models/elastic-search';

import s from './accommodation-tile-lite.module.css';
import a from './tile-amenities.module.css';
import { getIconComponent, TileIcon } from './tile-icon';

interface TileAmenitiesProps {
    amenities?: InternalAmenityGroup[];
    maxVisible?: number;
    isInquiry: boolean;
    t: AccommodationTileProps['t'];
}

const AmenityIcon = ({ type }: { type: AmenityGroupModel['type'] }) => {
    const icon = getIconComponent(getAmenityIcon(type as unknown as InternalAmenityGroupEnum));

    return icon ? <TileIcon icon={icon} className={s.amenityIcon} /> : null;
};

const StaticAmenities = ({
    sortedAmenities,
    maxVisible,
    isInquiry,
    getLabel,
    translations,
}: {
    sortedAmenities: AmenityGroupModel[];
    maxVisible: number;
    isInquiry: boolean;
    getLabel: (amenity: AmenityGroupModel) => string;
    translations: AmenityTranslations;
}) => {
    const visibleAmenities = sortedAmenities.slice(0, maxVisible);
    const hiddenCount = sortedAmenities.length - maxVisible;

    return (
        <ul className={isInquiry ? s.amenitiesStaticInquiry : s.amenitiesStatic}>
            {visibleAmenities.map((amenity, index) => (
                <li
                    key={`amenity-${amenity.type}-${index}`}
                    className={s.amenityStatic}
                    data-testid={`acc-tile-amenity-${amenity.type}`}
                >
                    <AmenityIcon type={amenity.type} />
                    <span className={s.amenityLabel}>{getLabel(amenity)}</span>
                </li>
            ))}

            {hiddenCount > 0 && (
                <li className={s.hideBelowMd}>
                    <div className={s.overlayTooltipRoot}>
                        <div className={s.amenitiesOverflowStaticChip}>
                            <span className={s.noUserSelect}>
                                +{hiddenCount} {getOverflowWord(hiddenCount, translations)}
                            </span>
                        </div>
                        <div
                            className={`${s.tooltipContent} ${s.amenitiesOverflowTooltip}`}
                            data-testid={'tooltip-content'}
                        >
                            <div>
                                {sortedAmenities.slice(maxVisible).map((amenity, i) => (
                                    <div key={i}>{getLabel(amenity)}</div>
                                ))}
                            </div>
                        </div>
                    </div>
                </li>
            )}
        </ul>
    );
};

const DynamicAmenities = ({
    sortedAmenities,
    isInquiry,
    getLabel,
}: {
    sortedAmenities: AmenityGroupModel[];
    isInquiry: boolean;
    getLabel: (amenity: AmenityGroupModel) => string;
}) => {
    const listRef = useRef<HTMLDivElement>(null);
    const amenityItemsRef = useRef<(HTMLDivElement | null)[]>([]);
    const overflowRef = useRef<HTMLDivElement>(null);

    const { isInitialized, hiddenLabels, overflowText } = useAmenitiesVisibility({
        listRef,
        amenityItemsRef,
        overflowRef,
        amenitiesCount: sortedAmenities.length,
    });

    // Reset before render so stale refs don't linger if the amenities list shrinks
    amenityItemsRef.current = [];

    const listClassName = isInquiry ? s.amenitiesDynamicInquiry : s.amenitiesDynamic;

    return (
        <ul
            ref={listRef as unknown as React.Ref<HTMLUListElement>}
            className={isInitialized ? listClassName : `${listClassName} ${a.pending}`}
        >
            {sortedAmenities.map((amenity, index) => {
                const label = getLabel(amenity);

                return (
                    <li
                        ref={(el: HTMLLIElement | null) => {
                            amenityItemsRef.current[index] = el as unknown as HTMLDivElement;
                        }}
                        key={`amenity-${amenity.type}-${index}`}
                        className={
                            index >= ACCOMMODATION_TILE.MAX_AMENITIES
                                ? s.amenityDynamicBeyondLimit
                                : s.amenityDynamic
                        }
                        data-testid={`acc-tile-amenity-${amenity.type}`}
                        data-amenity-label={label}
                    >
                        <AmenityIcon type={amenity.type} />
                        <span className={s.amenityLabel}>{label}</span>
                    </li>
                );
            })}

            <li
                ref={overflowRef as unknown as React.Ref<HTMLLIElement>}
                className={`${s.amenitiesOverflowTrigger} ${s.overlayTooltipRoot} ${a.pendingOverflow}`}
                onClick={event => event.stopPropagation()}
            >
                <span className={s.amenitiesOverflowChip}>{overflowText}</span>
                {hiddenLabels.length ? (
                    <div
                        className={`${s.tooltipContent} ${s.amenitiesOverflowTooltip}`}
                        data-testid={'tooltip-content'}
                    >
                        <div>
                            {hiddenLabels.map((hiddenLabel, i) => (
                                <div key={i}>{hiddenLabel}</div>
                            ))}
                        </div>
                    </div>
                ) : null}
            </li>
        </ul>
    );
};

/** Same markup as `AccommodationTileAmenities`, without Chakra runtime styling */
export const TileAmenities = ({ amenities, maxVisible, isInquiry, t }: TileAmenitiesProps) => {
    const sortedAmenities = useMemo(
        () =>
            amenities
                ? sortAmenities(filterAmenities(amenities as unknown as AmenityGroupModel[]))
                : undefined,
        [amenities],
    );

    if (!sortedAmenities) {
        return null;
    }

    const getLabel = (amenity: AmenityGroupModel): string =>
        amenity.type === InternalAmenityGroupEnum.Parking && amenity.free === true
            ? t('amenities.freeParking')
            : amenity.label;

    return maxVisible ? (
        <StaticAmenities
            sortedAmenities={sortedAmenities}
            maxVisible={maxVisible}
            isInquiry={isInquiry}
            getLabel={getLabel}
            translations={t('otherItems', { returnObjects: true }) as AmenityTranslations}
        />
    ) : (
        <DynamicAmenities
            sortedAmenities={sortedAmenities}
            isInquiry={isInquiry}
            getLabel={getLabel}
        />
    );
};
